// client/src/components/layout/Navbar.js
import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useQuote } from '../../context/QuoteContext';
import { PRODUCT_CATEGORIES } from '../../config/categories';

const toUrlSlug = (name) => {
  return name.toLowerCase().replace(/\s+/g, '-');
};

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileSubmenu, setMobileSubmenu] = useState(null);
  const [routeChanging, setRouteChanging] = useState(false);
  const { openModal, quoteItems } = useQuote();
  const location = useLocation();

  // Detect scroll for elevated sticky glassmorphic header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 24);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Trigger top navigation progress bar & close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
    setMobileSubmenu(null);
    setRouteChanging(true);
    const timer = setTimeout(() => {
      setRouteChanging(false);
    }, 520);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const getNavLinkClass = ({ isActive }) => {
    return isActive ? 'nav-link active-link' : 'nav-link';
  };

  const handleQuoteButtonClick = () => {
    openModal();
    setMenuOpen(false);
  };

  const toggleMobileSubmenu = (key, e) => {
    if (window.innerWidth <= 1024) {
      e.preventDefault();
      setMobileSubmenu((prev) => (prev === key ? null : key));
    }
  };

  return (
    <nav className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
      {/* Animated Top Route Transition Progress Bar */}
      <div
        className={`route-progress-bar ${routeChanging ? 'active' : ''}`}
        aria-hidden="true"
      ></div>

      <div className="container navbar-content">
        <div className="navbar-logo">
          <Link to="/" className="logo-link" onClick={() => setMenuOpen(false)}>
            <img src="/images/makah-medics-logo.jpeg" alt="Makah Medics Logo" />
            <span className="logo-text">MAKAH MEDICS</span>
          </Link>
        </div>

        <button
          type="button"
          className={`menu-icon ${menuOpen ? 'is-open' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={menuOpen}
        >
          <i className={menuOpen ? 'fas fa-times' : 'fas fa-bars'}></i>
        </button>

        {/* Backdrop overlay for mobile drawer */}
        {menuOpen && (
          <div
            className="navbar-backdrop"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          ></div>
        )}

        <ul className={menuOpen ? 'navbar-links active' : 'navbar-links'}>
          <li className="nav-item">
            <NavLink to="/" end className={getNavLinkClass} onClick={() => setMenuOpen(false)}>
              <span>Home</span>
            </NavLink>
          </li>

          <li className="nav-item">
            <NavLink to="/about" className={getNavLinkClass} onClick={() => setMenuOpen(false)}>
              <span>About Us</span>
            </NavLink>
          </li>

          <li
            className={`nav-item dropdown ${
              mobileSubmenu === 'products' ? 'mobile-expanded' : ''
            }`}
          >
            <div className="nav-link-with-toggle">
              <NavLink to="/products" className={getNavLinkClass}>
                <span>Products</span>
                <i
                  className="fas fa-chevron-down dropdown-chevron"
                  onClick={(e) => toggleMobileSubmenu('products', e)}
                ></i>
              </NavLink>
            </div>
            <div className="dropdown-content">
              {PRODUCT_CATEGORIES.map((category, idx) => (
                <Link
                  key={category}
                  to={`/products/${toUrlSlug(category)}`}
                  style={{ '--item-index': idx }}
                  onClick={() => setMenuOpen(false)}
                >
                  <span>{category}</span>
                  <i className="fas fa-arrow-right dropdown-item-arrow"></i>
                </Link>
              ))}
              <div className="dropdown-divider"></div>
              <Link
                to="/products"
                className="dropdown-view-all"
                style={{ '--item-index': PRODUCT_CATEGORIES.length }}
                onClick={() => setMenuOpen(false)}
              >
                <span>View All Products</span>
                <i className="fas fa-th-large dropdown-item-arrow"></i>
              </Link>
            </div>
          </li>

          <li
            className={`nav-item dropdown ${
              mobileSubmenu === 'services' ? 'mobile-expanded' : ''
            }`}
          >
            <div className="nav-link-with-toggle">
              <NavLink to="/services" className={getNavLinkClass}>
                <span>Services</span>
                <i
                  className="fas fa-chevron-down dropdown-chevron"
                  onClick={(e) => toggleMobileSubmenu('services', e)}
                ></i>
              </NavLink>
            </div>
            <div className="dropdown-content">
              <Link
                to="/services/maintenance-repair"
                style={{ '--item-index': 0 }}
                onClick={() => setMenuOpen(false)}
              >
                <span>Maintenance & Repair</span>
                <i className="fas fa-arrow-right dropdown-item-arrow"></i>
              </Link>
              <Link
                to="/services/consultation-training"
                style={{ '--item-index': 1 }}
                onClick={() => setMenuOpen(false)}
              >
                <span>Consultation & Training</span>
                <i className="fas fa-arrow-right dropdown-item-arrow"></i>
              </Link>
              <Link
                to="/services/managed-supply"
                style={{ '--item-index': 2 }}
                onClick={() => setMenuOpen(false)}
              >
                <span>Managed Supply</span>
                <i className="fas fa-arrow-right dropdown-item-arrow"></i>
              </Link>
            </div>
          </li>

          <li className="nav-item">
            <NavLink to="/contact" className={getNavLinkClass} onClick={() => setMenuOpen(false)}>
              <span>Contact Us</span>
            </NavLink>
          </li>

          <li className="mobile-only-cta">
            <button className="btn-request-quote" onClick={handleQuoteButtonClick}>
              <span>Request a Quote</span>
              {quoteItems.length > 0 && (
                <span className="quote-count-badge">{quoteItems.length}</span>
              )}
              <i className="fas fa-paper-plane"></i>
            </button>
          </li>
        </ul>

        <div className="navbar-cta">
          <button className="btn-request-quote" onClick={handleQuoteButtonClick}>
            <span>Request a Quote</span>
            {quoteItems.length > 0 && (
              <span className="quote-count-badge">{quoteItems.length}</span>
            )}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
